import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { useProgressReports } from "../../../hooks/useProgressReports";
import type { ProgressReportEntry } from "../../../server/progress-reports";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { queryKeys, STALE_TIMES } from "../../../lib/queryClient";
import { getProgressReports } from "../../../server/progress-reports";
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/progress-reports/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  loader: async ({ context }) => {
    if (typeof window === "undefined") return;
    const { session } = context as RouterContextWithSession;
    if (!session) return;

    await context.queryClient.ensureQueryData({
      queryKey: queryKeys.progressReports.list(1, 10),
      queryFn: async () => {
        const result = await getProgressReports({
          data: { accessToken: session.access_token, page: 1, limit: 10 },
        });
        if (result.error) throw new Error(result.error);
        return result.data;
      },
      staleTime: STALE_TIMES.system,
    });
  },
  pendingComponent: () => <PageSkeleton rows={4} variant="cards" />,
  errorComponent: createRouteError("Progress Reports"),
  component: ProgressReportsPage,
});

function ProgressReportsPage() {
  const { session, loading, isAuthenticated } = useAuth();

  if (loading || !isAuthenticated || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return <AuthenticatedProgressReports accessToken={session.access_token} />;
}

const REPORT_TYPES = [
  { value: undefined, label: "All Types" },
  { value: "daily" as const, label: "Daily" },
  { value: "weekly" as const, label: "Weekly" },
  { value: "monthly" as const, label: "Monthly" },
];

function AuthenticatedProgressReports({ accessToken }: { accessToken: string }) {
  const [page, setPage] = useState(1);
  const [reportType, setReportType] = useState<"daily" | "weekly" | "monthly" | undefined>(undefined);
  const limit = 20;

  const { data, isLoading, error } = useProgressReports(
    { accessToken },
    { page, limit, reportType }
  );

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  return (
    <div className="text-foreground">
      <div className="container mx-auto px-4 py-6 max-w-5xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Progress Reports</h1>
            <p className="text-muted-foreground">
              Team analytics and performance reporting
            </p>
          </div>
          <Button variant="outline" asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Report History</CardTitle>
            <CardDescription>
              {data ? `${data.total} reports generated` : "Loading..."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {REPORT_TYPES.map((t) => (
                <Button
                  key={t.label}
                  variant={reportType === t.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setReportType(t.value);
                    setPage(1);
                  }}
                >
                  {t.label}
                </Button>
              ))}
            </div>

            {isLoading && (
              <p className="text-muted-foreground">Loading reports...</p>
            )}
            {error && (
              <p className="text-destructive">Error: {error.message}</p>
            )}
            {!isLoading && !error && (!data || data.reports.length === 0) && (
              <p className="text-muted-foreground">No reports found. Generate a report to get started.</p>
            )}
            {!isLoading && !error && data && data.reports.length > 0 && (
              <div className="space-y-4">
                <ul className="space-y-2">
                  {data.reports.map((r: ProgressReportEntry) => (
                    <li
                      key={r.id}
                      className="rounded-lg border bg-card p-4 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium capitalize">{r.report_type} Report</p>
                          <p className="text-xs text-muted-foreground">
                            Generated {new Date(r.generated_at).toLocaleString()}
                            {r.sent_at && ` · Sent ${new Date(r.sent_at).toLocaleString()}`}
                          </p>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            r.delivery_status === "delivered"
                              ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                              : r.delivery_status === "failed"
                                ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                                : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {r.delivery_status ?? "pending"}
                        </span>
                      </div>
                      {r.ai_insights && (
                        <p className="text-sm text-muted-foreground border-t pt-2">
                          {r.ai_insights}
                        </p>
                      )}
                      {r.delivery_channels && r.delivery_channels.length > 0 && (
                        <div className="flex gap-1">
                          {r.delivery_channels.map((ch: string) => (
                            <span key={ch} className="text-xs bg-muted rounded px-1.5 py-0.5">
                              {ch}
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
